import { Icon } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useOverflowTooltip } from "../../../../utils/field";
import { TextFieldProps } from "./TextField";
import { containsOnlyNumbers } from "./utils";
import { ErrorMessages } from "../../types/fieldTypes";
/**Hooks for Text Field*/
export const useTextField = (props: TextFieldProps) => {
  const localValueRef = useRef<string | undefined>(props.value);
  const [isMasked, setIsMasked] = useState<boolean>(false);
  const textInputRef = useRef<HTMLInputElement | null>(null);
  const isTooltipVisible = useOverflowTooltip(
    textInputRef.current,
    props.value ?? ""
  );
  const [initialized, setInitialized] = useState<boolean>(false);
  const isUserInputRef = useRef<boolean>(false); // Track if change is from user input

  // Initialize
  useEffect(() => {
    if (!!textInputRef.current && !initialized) {
      setInitialized(true);
      setAndSanitize(props.value);
      localValueRef.current = props.value;
    }
    if (!!props.isSensitive) {
      setIsMasked(true);
    }
  }, []);

  useEffect(() => {
    // Only sync if the change didn't originate from user input
    if (props.value !== localValueRef.current && !isUserInputRef.current) {
      localValueRef.current = props.value;
    }
    // Reset the flag after processing
    isUserInputRef.current = false;
  }, [props.value]);

  const setAndSanitize = (newValue: string | undefined) => {
    if (props.sanitize && !!newValue) {
      newValue = props.sanitize(newValue);
    }
    return newValue;
  };

  const setMaskOnBlur = () => {
    if (!!props.isSensitive) {
      setIsMasked(true);
    }
  };

  const determineInputType = () => {
    return isMasked ? "password" : props.type;
  };

  /**Do not show tooltip if sensitive and Disabled*/
  const isSensitiveAndDisabled = () => {
    return props.disabled && props.isSensitive;
  };

  /**Show Only if is Sensitive info, has value, and not disabled*/
  const shouldShowMaskedIcon = () => {
    if (!!props.isSensitive && !!localValueRef.current) {
      const icon = isMasked ? IconNames.EyeOpen : IconNames.EyeOff;

      return (
        <Icon
          className="show-password-icon"
          icon={icon}
          onClick={() => setIsMasked(!isMasked)}
        />
      );
    }
  };

  const handleOnChange = (
    onchangeValue: string,
    submitValue: boolean = true
  ) => {
    let sanitizedValue = onchangeValue;
    if (props.inputMode === "numeric") {
      if (!containsOnlyNumbers(onchangeValue)) {
        setAndSanitize(onchangeValue.replace(/\D/g, ""));
        sanitizedValue = onchangeValue.replace(/\D/g, "");
      }
    } else {
      const sanitized = setAndSanitize(onchangeValue);
      sanitizedValue = sanitized ?? "";
    }

    // Mark that this change is from user input
    if (submitValue) {
      isUserInputRef.current = true;
    }

    localValueRef.current = sanitizedValue;

    if (submitValue) {
      props.onSubmit && props.onSubmit(sanitizedValue);
    }
  };

  const isFieldTooShort = useMemo(() => {
    const localValue = localValueRef.current?.toString() ?? "";
    if (!localValue){
      return false;
    }
    const lengthWithWhitespaceConsidered =
      (props.includeWhitespaceInMinLength
        ? localValue?.length
        : localValue?.replace(/\s/g, "").length) ?? 0;
    return props.minLength && lengthWithWhitespaceConsidered < props.minLength;
  }, [
    props.minLength,
    props.includeWhitespaceInMinLength,
    localValueRef.current,
  ]);

  const showRequiredBackground = useMemo(() => {
    const localValue = localValueRef.current;

    // If the field is required and there is no value, show the required background.
    if (props.isRequired && !localValue) {
      return true;
    }

    // If the field has a min length and the value is less than the min length, show the required background.
    return isFieldTooShort;
  }, [props.isRequired, isFieldTooShort, localValueRef.current]);

  // This returns the server errors if they exist, or the local ones if they do not.
  const errorMessages: ErrorMessages = useMemo(() => {
    if (props.errorMessages && props.errorMessages.length > 0) {
      return props.errorMessages;
    }

    if (isFieldTooShort) {
      return [
        `Response must be at least ${props.minLength} ${
          props.includeWhitespaceInMinLength ||
          !localValueRef.current?.match(/\s/)
            ? "characters"
            : "characters, not including whitespace"
        }.`,
      ];
    }

    return [];
  }, [
    props.errorMessages,
    isFieldTooShort,
    props.includeWhitespaceInMinLength,
  ]);

  return {
    localValue: localValueRef.current,
    isTooltipVisible: isTooltipVisible,
    textInputRef,
    isSensitiveAndDisabled,
    determineInputType,
    setMaskOnBlur,
    shouldShowMaskedIcon,
    handleOnChange,
    showRequiredBackground,
    errorMessages,
  };
};
